import type { SupabaseClient } from '@supabase/supabase-js';
import { derivarStatusBoleto, valorDevido } from './statusBoleto.ts';
import type { DadosStatusBoleto } from './statusBoleto.ts';

/**
 * Troca de dono da UC — o cliente vende o estabelecimento e o comprador continua
 * na Vigor (desenho de 23/09/2026, migration 20260923205226).
 *
 * A UC continua sendo uma só. O vendedor pede a troca dentro da negociação nova;
 * a UC passa a aparecer nela (termo, simulação e análise leem por lá), mas quem
 * paga continua sendo o dono anterior até a análise da negociação nova ser
 * aprovada. Na aprovação o banco efetiva sozinho: carimba as faturas e os
 * acordos do dono anterior (`dono_anterior_troca_id`) e passa o cadastro da UC.
 *
 * Aqui ficam as regras que as telas precisam repetir igual: quem paga enquanto o
 * pedido está aberto, de quem é cada fatura e quanto o dono anterior deixou em
 * aberto. Sem JSX, roda sob `node --test`.
 */

export type StatusTrocaDono = 'pendente' | 'efetivada' | 'expirada' | 'cancelada';

/**
 * De onde veio a troca. 'venda' é o pedido do CRM descrito acima. 'aditivo' é o
 * aditivo contratual do Financeiro que troca o titular (#6238): já nasce
 * efetivada, a UC continua na mesma negociação e não há negociação de origem —
 * a fatura carimbada usa o cadastro guardado em `dono_anterior` como pagador.
 */
export type OrigemTrocaDono = 'venda' | 'aditivo';

/** Cadastro da UC no momento do pedido (coluna `dono_anterior`). */
export interface DonoAnteriorSnapshot {
  nome_cliente?: string | null;
  cpf_cnpj_cliente?: string | null;
  whatsapp_cliente?: string | null;
  email_cliente?: string | null;
  desconto_tarifa?: number | string | null;
  desconto_bandeira?: number | string | null;
  carencia?: string | null;
  titular_fatura?: string | null;
  cpf_cnpj_titular?: string | null;
  negociacao_cliente_nome?: string | null;
  negociacao_cpf_cnpj?: string | null;
  negociacao_responsavel_id?: string | null;
}

export interface TrocaDonoUc {
  id: string;
  uc_id: number;
  numero_uc: string | null;
  negociacao_origem_id: number | null;
  negociacao_destino_id: number | null;
  justificativa: string;
  status: StatusTrocaDono;
  /** Ausente em linha lida antes da migration 20261012120000: vale 'venda'. */
  origem?: OrigemTrocaDono;
  dono_anterior: DonoAnteriorSnapshot;
  condicoes_novas: { desconto_tarifa?: number; desconto_bandeira?: number; carencia?: string };
  mes_corte: string | null;
  erro: string | null;
  solicitado_por: string | null;
  solicitado_por_nome: string | null;
  created_at: string;
  expira_em: string;
  efetivada_em: string | null;
  encerrada_em: string | null;
  motivo_encerramento: string | null;
}

export const JUSTIFICATIVA_MINIMA = 10;

/** Mensagem de erro da justificativa, ou `null` se está boa. Mesma regra do banco. */
export const validarJustificativaTroca = (texto: string | null | undefined): string | null => {
  const t = (texto ?? '').trim();
  if (t.length < JUSTIFICATIVA_MINIMA) {
    return `Escreva a justificativa da troca (pelo menos ${JUSTIFICATIVA_MINIMA} letras). Ex.: "Vendeu a padaria para a Maria em setembro".`;
  }
  return null;
};

const soDigitos = (v: string | null | undefined): string => (v ?? '').replace(/\D/g, '');
const nomeNormalizado = (v: string | null | undefined): string =>
  (v ?? '').normalize('NFD').replace(/\p{M}/gu, '').trim().toUpperCase();

const numeroOuNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export interface ContextoCobranca {
  /** Negociação cujo assinante paga esta fatura. */
  negociacaoId: number | null;
  /** "Cadastro da UC" que vale para esta fatura (reserva do pagador). */
  cadastro: { nome: string | null; documento: string | null };
  descontos: { desconto_tarifa: number | null; desconto_bandeira: number | null };
  /** false quando a fatura não é de quem está hoje no cadastro da UC: o desconto
   *  dela não pode ser gravado de volta na UC. */
  descontoEhDaUc: boolean;
  /** A troca que mudou quem paga, se houver. */
  troca: TrocaDonoUc | null;
}

/**
 * Quem paga a fatura e com que desconto, considerando a troca de dono.
 *
 * - Pedido aberto: a UC já está na negociação nova e já carrega o desconto do
 *   comprador (é o que vai no termo dele), mas a fatura ainda é do dono anterior.
 *   O cálculo põe o assinante da negociação à frente do cadastro da UC como
 *   pagador — sem isto o boleto do antigo sairia no nome do comprador antes da
 *   análise dele.
 * - Fatura carimbada (`dono_anterior_troca_id`), numa reemissão depois da troca:
 *   continua do dono anterior, com os dados guardados no pedido.
 * - Sem troca no caminho: o de sempre, UC e negociação dela.
 */
export const contextoCobrancaDaFatura = (
  uc: {
    negociacao_id?: number | null;
    nome_cliente?: string | null;
    cpf_cnpj_cliente?: string | null;
    desconto_tarifa?: number | null;
    desconto_bandeira?: number | null;
  },
  fatura: { dono_anterior_troca_id?: string | null },
  trocas: TrocaDonoUc[]
): ContextoCobranca => {
  const daFatura = fatura.dono_anterior_troca_id
    ? trocas.find(t => t.id === fatura.dono_anterior_troca_id)
    : undefined;
  const troca = daFatura ?? trocas.find(t => t.status === 'pendente') ?? null;
  if (!troca) {
    return {
      negociacaoId: uc.negociacao_id ?? null,
      cadastro: { nome: uc.nome_cliente ?? null, documento: uc.cpf_cnpj_cliente ?? null },
      descontos: { desconto_tarifa: uc.desconto_tarifa ?? null, desconto_bandeira: uc.desconto_bandeira ?? null },
      descontoEhDaUc: true,
      troca: null,
    };
  }
  const d = troca.dono_anterior ?? {};
  return {
    negociacaoId: troca.negociacao_origem_id,
    cadastro: { nome: d.nome_cliente ?? null, documento: d.cpf_cnpj_cliente ?? null },
    descontos: {
      desconto_tarifa: numeroOuNull(d.desconto_tarifa),
      desconto_bandeira: numeroOuNull(d.desconto_bandeira),
    },
    descontoEhDaUc: false,
    troca,
  };
};

/** A fatura é do dono anterior da UC (carimbada na troca). */
export const ehFaturaDoDonoAnterior = (f: { dono_anterior_troca_id?: string | null }): boolean =>
  !!f.dono_anterior_troca_id;

/**
 * Nomes "da UC" que a busca de Faturas compara com o texto digitado, além dos
 * nomes da própria fatura.
 *
 * Fatura do dono anterior (carimbada): valem os nomes guardados na troca, não os
 * do cadastro — o cadastro agora é de quem entrou, e buscar o dono novo traria
 * as faturas do antigo. Troca que não veio na lista (RLS, falha ao carregar):
 * nenhum nome da UC — a fatura ainda casa pelo nome impresso nela.
 */
export const nomesDaUcNaFatura = (
  fatura: { dono_anterior_troca_id?: string | null },
  uc: { nome_cliente?: string | null; titular_fatura?: string | null } | null | undefined,
  donosAnteriores: ReadonlyMap<string, DonoAnteriorSnapshot>,
): string[] => {
  const d = fatura.dono_anterior_troca_id ? donosAnteriores.get(fatura.dono_anterior_troca_id) : undefined;
  const nomes = fatura.dono_anterior_troca_id
    ? [d?.nome_cliente, d?.titular_fatura, d?.negociacao_cliente_nome]
    : [uc?.nome_cliente, uc?.titular_fatura];
  return nomes.map(n => (n ?? '').trim()).filter(Boolean);
};

/**
 * Nome e CPF que a fatura deve levar no cálculo, quando a UC já trocou de dono.
 *
 * Até a Equatorial trocar o titular, a conta de luz do dono novo chega com o nome
 * e o CPF do antigo — e a inadimplência agrupa pelo nome da fatura, então a
 * dívida do dono novo apareceria no nome do antigo. Devolve o cliente atual da UC
 * quando a fatura (não carimbada) veio no nome de um dono anterior; `null` para
 * manter o que está na fatura.
 */
export const clienteDaFaturaAposTroca = (
  fatura: { cliente_nome?: string | null; cliente_cpf_cnpj?: string | null; dono_anterior_troca_id?: string | null },
  uc: { nome_cliente?: string | null; cpf_cnpj_cliente?: string | null },
  trocasEfetivadas: Pick<TrocaDonoUc, 'dono_anterior'>[]
): { nome: string; documento: string } | null => {
  if (ehFaturaDoDonoAnterior(fatura) || trocasEfetivadas.length === 0) return null;
  const nomeAtual = (uc.nome_cliente ?? '').trim();
  const docAtual = soDigitos(uc.cpf_cnpj_cliente);
  if (!nomeAtual || !docAtual) return null;

  const docFatura = soDigitos(fatura.cliente_cpf_cnpj);
  if (docFatura && docFatura === docAtual) return null;

  const docsAntigos = new Set<string>();
  const nomesAntigos = new Set<string>();
  for (const t of trocasEfetivadas) {
    const d = t.dono_anterior ?? {};
    [d.cpf_cnpj_cliente, d.negociacao_cpf_cnpj, d.cpf_cnpj_titular].map(soDigitos).filter(Boolean).forEach(x => docsAntigos.add(x));
    [d.nome_cliente, d.negociacao_cliente_nome, d.titular_fatura].map(nomeNormalizado).filter(Boolean).forEach(x => nomesAntigos.add(x));
  }

  const veioNoNomeDoAntigo = docFatura
    ? docsAntigos.has(docFatura)
    : nomesAntigos.has(nomeNormalizado(fatura.cliente_nome));
  return veioNoNomeDoAntigo ? { nome: nomeAtual, documento: docAtual } : null;
};

export interface DividaDonoAnterior {
  valor: number;
  faturas: number;
  vencidas: number;
}

/**
 * Quanto o dono anterior deixou sem pagar nas faturas da UC: boletos vencidos e
 * em aberto. Mostrado na análise do comprador — não trava nada (a dívida fica
 * com o antigo), mas ajuda a pegar "venda" para parente só para limpar o nome.
 */
export const dividaDoDonoAnterior = (faturas: DadosStatusBoleto[], hoje: Date = new Date()): DividaDonoAnterior => {
  let valor = 0;
  let quantas = 0;
  let vencidas = 0;
  for (const f of faturas) {
    const status = derivarStatusBoleto(f, hoje);
    if (status !== 'Vencido' && status !== 'Em aberto') continue;
    valor += valorDevido(f);
    quantas += 1;
    if (status === 'Vencido') vencidas += 1;
  }
  return { valor: Math.round(valor * 100) / 100, faturas: quantas, vencidas };
};

/** Por que a fatura carimbada não é de quem está hoje na UC — completa "Esta fatura é de Fulano, ...". */
export const comoODonoAnteriorSaiu = (t: Pick<TrocaDonoUc, 'origem'>): string =>
  t.origem === 'aditivo' ? 'titular da UC antes do aditivo contratual' : 'que vendeu o estabelecimento';

/** Frase curta do estado do pedido, do ponto de vista de quem está olhando. */
export const descreverTroca = (t: TrocaDonoUc, lado: 'novo' | 'antigo'): string => {
  const antigo = t.dono_anterior?.nome_cliente?.trim() || 'o dono anterior';
  const uc = t.numero_uc || 'a UC';
  if (t.origem === 'aditivo' && t.status === 'efetivada') {
    const corte = t.mes_corte ? `a partir de ${t.mes_corte}` : 'a partir da próxima fatura';
    return `UC ${uc} mudou de titular por aditivo contratual (era de ${antigo}). É cobrada do novo titular ${corte}; faturas e acordos anteriores continuam com ${antigo}.`;
  }
  switch (t.status) {
    case 'pendente':
      return lado === 'novo'
        ? `UC ${uc} em troca de dono (vinda de ${antigo}). Enquanto a análise desta negociação não for aprovada, a UC continua sendo cobrada de ${antigo}.`
        : `UC ${uc} em troca de dono para a negociação #${t.negociacao_destino_id}. A cobrança segue neste cliente até a análise de lá ser aprovada.`;
    case 'efetivada': {
      const corte = t.mes_corte ? `a partir de ${t.mes_corte}` : 'a partir da próxima fatura';
      return lado === 'novo'
        ? `UC ${uc} veio de ${antigo} (venda do estabelecimento). É cobrada desta negociação ${corte}; faturas e acordos anteriores continuam com ${antigo}.`
        : `UC ${uc} passou para a negociação #${t.negociacao_destino_id} (venda do estabelecimento). As faturas e dívidas até a troca continuam aqui.`;
    }
    case 'expirada':
    case 'cancelada':
      return `Pedido de troca de dono da UC ${uc} encerrado sem troca: ${t.motivo_encerramento || t.status}.`;
  }
};

// ─── Acesso ao banco ─────────────────────────────────────────────────────────

type Cliente = Pick<SupabaseClient, 'from' | 'rpc'>;

// '*' de propósito: a coluna `origem` chegou depois (migration 20261012120000) e
// pedi-la pelo nome num banco sem ela derrubaria a leitura — e quem lê as trocas
// (cálculo e emissão) trata erro como "sem troca", o que erraria o pagador.
const COLUNAS = '*';

export interface PedidoTrocaDono {
  negociacaoDestinoId: number;
  ucId: number;
  justificativa: string;
  /** Frações (0.20 = 20%), como na UC. */
  descontoTarifa?: number | null;
  descontoBandeira?: number | null;
  carencia?: string | null;
}

export async function solicitarTrocaDonoUc(client: Cliente, p: PedidoTrocaDono): Promise<string> {
  const { data, error } = await client.rpc('solicitar_troca_dono_uc', {
    p_negociacao_destino_id: p.negociacaoDestinoId,
    p_uc_id: p.ucId,
    p_justificativa: p.justificativa.trim(),
    p_desconto_tarifa: p.descontoTarifa ?? null,
    p_desconto_bandeira: p.descontoBandeira ?? null,
    p_carencia: p.carencia ?? null,
  });
  if (error) throw new Error(error.message || 'Não foi possível pedir a troca de dono.');
  return data as string;
}

export async function cancelarTrocaDonoUc(client: Cliente, trocaId: string, motivo: string): Promise<void> {
  const { error } = await client.rpc('cancelar_troca_dono_uc', { p_troca_id: trocaId, p_motivo: motivo });
  if (error) throw new Error(error.message || 'Não foi possível cancelar a troca de dono.');
}

/** Trocas em que a negociação é a nova ou a antiga, mais recentes primeiro. */
export async function buscarTrocasDaNegociacao(client: Cliente, negociacaoId: number): Promise<TrocaDonoUc[]> {
  const { data, error } = await client
    .from('uc_trocas_dono')
    .select(COLUNAS)
    .or(`negociacao_destino_id.eq.${negociacaoId},negociacao_origem_id.eq.${negociacaoId}`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TrocaDonoUc[];
}

/** Cadastro guardado de cada troca efetivada, pelo id da troca (é o que a fatura carimbada aponta). */
export async function buscarDonosAnteriores(client: Cliente): Promise<Map<string, DonoAnteriorSnapshot>> {
  const { data, error } = await client
    .from('uc_trocas_dono')
    .select('id, dono_anterior')
    .eq('status', 'efetivada');
  if (error) throw error;
  return new Map((data ?? []).map((t: any) => [String(t.id), (t.dono_anterior ?? {}) as DonoAnteriorSnapshot]));
}

/** Trocas da UC, mais recentes primeiro. */
export async function buscarTrocasDaUc(client: Cliente, ucId: number): Promise<TrocaDonoUc[]> {
  const { data, error } = await client
    .from('uc_trocas_dono')
    .select(COLUNAS)
    .eq('uc_id', ucId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TrocaDonoUc[];
}
